// Intent: "Szkoła finansów" content — 14 lessons drafted with AI (Fable) on
// 2026-07-12 for owner+wife to EDIT later; bundled with the app (no backend).
// Educational only — never investment/tax advice (lessons 11-12 say so).

export interface SchoolLesson {
  day: number;
  title: string;
  teaser: string;
  sections: { heading: string; body: string }[];
  task: string;
  // Sources: names of books / institutions only - NEVER clickable URLs.
  // External links rot (owner: lesson-1 NBP portal 404'd); a title is durable.
  sources: string[];
}

export const SCHOOL_LESSONS: SchoolLesson[] = [
  {
    day: 1,
    title: "Po co budżet domowy? Poznaj metodę 50/30/20",
    teaser:
      "Budżet to nie tabelka dla księgowych — to mapa, która pokazuje, dokąd naprawdę idą Wasze pieniądze. Zaczniemy od najprostszej metody świata: 50/30/20.",
    sections: [
      {
        heading: "Dlaczego pieniądze „znikają”",
        body:
          "Znasz to uczucie? Wypłata przyszła, a dwa tygodnie później zastanawiasz się, gdzie się podziała. To nie znaczy, że zarabiacie za mało — najczęściej znaczy, że nikt nie pilnuje, dokąd pieniądze płyną.\n\nBudżet domowy to nic innego jak plan: ile wpływa, ile wypływa i na co. Nie musi być idealny. Musi po prostu być.",
      },
      {
        heading: "Metoda 50/30/20 w praktyce",
        body:
          "Podziel dochód rodziny na trzy koszyki. 50% na potrzeby: mieszkanie, jedzenie, rachunki, dojazdy, leki. 30% na przyjemności: restauracje, subskrypcje, hobby, prezenty. 20% na przyszłość: oszczędności i spłatę długów.\n\nPrzykład: rodzina z dochodem 7000 zł na rękę. Potrzeby: maksymalnie 3500 zł. Przyjemności: 2100 zł. Oszczędności: 1400 zł. Proporcje możecie dostosować — ważne, żeby trzeci koszyk nigdy nie był pusty.",
      },
      {
        heading: "Zacznij od jednego miesiąca",
        body:
          "Nie planuj od razu całego roku. Spójrz na ostatni miesiąc: zsumuj wpływy, potem wydatki z wyciągu bankowego. Przypisz je do trzech koszyków.\n\nU większości rodzin pierwszy szok wygląda tak samo: „przyjemności” zjadają dużo więcej niż 30%. I to jest dobra wiadomość — bo właśnie tam najłatwiej coś odzyskać.",
      },
    ],
    task:
      "Otwórz wyciąg z konta za ostatni miesiąc i zsumuj wydatki w trzech koszykach: potrzeby, przyjemności, przyszłość. Zapisz trzy kwoty — to Wasz punkt startu.",
    sources: [
      "Elizabeth Warren, Amelia Warren Tyagi — „All Your Worth: The Ultimate Lifetime Money Plan” (2005) — źródło reguły 50/30/20",
      "Narodowy Bank Polski (edukacja ekonomiczna)",
    ],
  },
  {
    day: 2,
    title: "Poduszka bezpieczeństwa — ile i gdzie ją trzymać",
    teaser:
      "Zepsuta pralka, chore dziecko, utrata pracy — życie lubi zaskakiwać. Poduszka finansowa sprawia, że niespodzianka to kłopot, a nie katastrofa.",
    sections: [
      {
        heading: "Ile to jest „wystarczająco”",
        body:
          "Zasada jest prosta: od 3 do 6 miesięcy podstawowych kosztów życia rodziny. Nie dochodów — kosztów. Jeśli na czynsz, jedzenie, rachunki i raty wydajecie 5000 zł miesięcznie, celujcie w 15 000–30 000 zł.\n\nTa kwota może przerażać. Dlatego zacznijcie od mniejszego celu: 1000 zł. Potem jeden miesiąc kosztów. Potem kolejny. Każdy szczebel realnie zwiększa Wasz spokój.",
      },
      {
        heading: "Gdzie trzymać poduszkę",
        body:
          "Poduszka musi spełniać dwa warunki: być bezpieczna i szybko dostępna. Najlepiej sprawdza się konto oszczędnościowe albo krótka lokata w banku.\n\nNie trzymajcie jej w gotówce w szufladzie (traci na inflacji i łatwo ją „pożyczyć” samemu sobie) ani w inwestycjach (mogą akurat być na minusie, gdy będą potrzebne). Depozyty w polskich bankach chroni Bankowy Fundusz Gwarancyjny — do równowartości 100 000 euro na osobę w jednym banku.",
      },
      {
        heading: "Jedna żelazna zasada",
        body:
          "Poduszka jest tylko na prawdziwe awarie: utrata pracy, zdrowie, niezbędne naprawy. Promocja na telewizor to nie awaria.\n\nJeśli musieliście z niej skorzystać — nic złego się nie stało, od tego jest. Po prostu w kolejnych miesiącach odbudujcie ją w pierwszej kolejności.",
      },
    ],
    task:
      "Policz miesięczne koszty życia Waszej rodziny (same niezbędne wydatki) i pomnóż razy 3. Zapisz tę kwotę jako cel poduszki — i otwórz konto oszczędnościowe, jeśli jeszcze go nie macie.",
    sources: [
      "Bankowy Fundusz Gwarancyjny — gwarancje depozytów",
      "Narodowy Bank Polski (edukacja ekonomiczna)",
    ],
  },
  {
    day: 3,
    title: "Najpierw płać sobie",
    teaser:
      "Większość z nas oszczędza to, co zostanie na koniec miesiąca — czyli zwykle nic. Dziś odwracamy kolejność, i to jest najskuteczniejszy trik w finansach domowych.",
    sections: [
      {
        heading: "Dlaczego „odłożę, co zostanie” nie działa",
        body:
          "Pieniądze na koncie mają dziwną właściwość: zawsze znajdą sobie zajęcie. Jeśli oszczędzanie planujecie na koniec miesiąca, konkuruje ono z pizzą, promocjami i „drobnymi” zakupami. I prawie zawsze przegrywa.\n\n„Najpierw płać sobie” znaczy: oszczędności traktujesz jak najważniejszy rachunek — taki jak czynsz. Płacisz go pierwszego dnia, nie ostatniego.",
      },
      {
        heading: "Automat robi robotę",
        body:
          "Ustaw w bankowości zlecenie stałe: dzień po wypłacie określona kwota sama przechodzi na konto oszczędnościowe. Bez decyzji, bez silnej woli, bez pamiętania.\n\nIle? Docelowo 10–20% dochodu, ale zacznijcie od kwoty, która nie boli: 100 czy 200 zł. Rodzina odkładająca automatycznie 300 zł miesięcznie po roku ma 3600 zł — często pierwszą poduszkę bezpieczeństwa w życiu.",
      },
      {
        heading: "Podnoś poprzeczkę bezboleśnie",
        body:
          "Najlepszy moment na zwiększenie kwoty? Podwyżka. Jeśli pensja rośnie o 400 zł, przekieruj od razu połowę na oszczędności — i tak jeszcze nie zdążyliście się przyzwyczaić do wyższych wydatków.\n\nDziała to też przy spłaconym długu: rata zniknęła z budżetu, więc ta sama kwota może płynąć na Wasze konto zamiast do banku.",
      },
    ],
    task:
      "Ustaw dziś w aplikacji banku zlecenie stałe na konto oszczędnościowe — na dzień po wypłacie, na kwotę, którą na pewno udźwigniecie (nawet 100 zł).",
    sources: [
      "Narodowy Bank Polski (edukacja ekonomiczna)",
    ],
  },
  {
    day: 4,
    title: "Opłaty stałe pod lupą — audyt subskrypcji i rachunków",
    teaser:
      "Streaming, siłownia, telefon, pakiety, które „kiedyś się przydadzą” — małe opłaty potrafią zjadać setki złotych miesięcznie. Dziś robimy porządek.",
    sections: [
      {
        heading: "Ciche wycieki z budżetu",
        body:
          "Subskrypcje są sprytne: każda z osobna kosztuje „tylko” 20–40 zł, więc żadnej nie żal. Ale trzy platformy streamingowe, dwie aplikacje, siłownia i pakiet w telefonie to razem często 300–400 zł miesięcznie. Rocznie: 4000 zł i więcej.\n\nPytanie nie brzmi „czy nas na to stać”, tylko „czy naprawdę z tego korzystamy”.",
      },
      {
        heading: "Audyt w trzech krokach",
        body:
          "Krok 1: weź wyciągi z konta i karty za ostatnie dwa miesiące i wypisz każdą powtarzającą się opłatę. Krok 2: przy każdej odpowiedz szczerze — korzystaliśmy w ostatnim miesiącu? Krok 3: wszystko, co dostało „nie”, anuluj dziś.\n\nWiele usług można wyłączyć i włączyć ponownie za miesiąc bez żadnej kary. Nic nie tracicie — testujecie, czy Wam ich brakuje.",
      },
      {
        heading: "Rachunki też da się negocjować",
        body:
          "Telefon, internet, telewizja — po zakończeniu umowy operatorzy często przełączają Was na droższą taryfę. Jeden telefon do infolinii ze zdaniem „rozważam zmianę operatora, co możecie zaproponować?” potrafi obniżyć rachunek o 20–50 zł miesięcznie.\n\nPamiętajcie też: przy umowach zawieranych na odległość macie 14 dni na odstąpienie bez podania przyczyny.",
      },
    ],
    task:
      "Wypisz wszystkie cykliczne opłaty z ostatnich dwóch miesięcy i anuluj przynajmniej jedną, z której realnie nie korzystacie. Zapisz, ile zaoszczędzicie rocznie.",
    sources: [
      "UOKiK — prawa konsumenta",
    ],
  },
  {
    day: 5,
    title: "Długi: dobre, złe i metoda kuli śnieżnej",
    teaser:
      "Nie każdy dług to problem — ale niektóre potrafią rozłożyć budżet rodziny na łopatki. Dziś nauczysz się je rozróżniać i poznasz plan wyjścia z tych złych.",
    sections: [
      {
        heading: "Dobry dług, zły dług",
        body:
          "Dobry dług buduje Waszą przyszłość i ma rozsądny koszt: kredyt hipoteczny na mieszkanie, w którym żyjecie, czy kredyt na naukę zwiększającą zarobki.\n\nZły dług finansuje bieżącą konsumpcję i jest drogi: chwilówki, zakupy na raty „0%”, których nikt nie doczytał, przekroczony limit na karcie kredytowej. Porównując oferty, patrzcie zawsze na RRSO — rzeczywistą roczną stopę oprocentowania, która pokazuje pełny koszt, nie tylko odsetki.",
      },
      {
        heading: "Kula śnieżna: od najmniejszego",
        body:
          "Jeśli macie kilka długów, wypiszcie je od najmniejszego do największego. Wszystkie spłacacie w minimalnych ratach, a każdą wolną złotówkę kierujecie na ten najmniejszy.\n\nGdy zniknie — jego ratę doliczacie do spłaty kolejnego. Kula rośnie z każdym zamkniętym długiem. Matematycznie czasem lepiej zacząć od najdroższego, ale kula śnieżna daje coś cenniejszego: szybkie zwycięstwa, które trzymają motywację.",
      },
      {
        heading: "Poznaj swoją sytuację",
        body:
          "Nie wiecie dokładnie, ile i komu jesteście winni? Sprawdźcie swój raport w Biurze Informacji Kredytowej — zobaczycie tam wszystkie kredyty i swoją historię spłat.\n\nJeśli rata zaczyna Was przerastać, nie chowajcie głowy w piasek: kontakt z bankiem i prośba o restrukturyzację to normalna, legalna droga. Im wcześniej, tym więcej opcji.",
      },
    ],
    task:
      "Wypisz wszystkie długi rodziny: kwota, rata, oprocentowanie. Ustaw je od najmniejszego do największego — to Wasza kolejność spłaty metodą kuli śnieżnej.",
    sources: [
      "Biuro Informacji Kredytowej",
      "Rzecznik Finansowy — pomoc dla klientów instytucji finansowych",
    ],
  },
  {
    day: 6,
    title: "Ubezpieczenia — od czego naprawdę chronią",
    teaser:
      "Ubezpieczenie to nie koszt, tylko tarcza: płacisz niewielką, przewidywalną kwotę, żeby wielki, nieprzewidywalny wydatek nie zniszczył budżetu rodziny.",
    sections: [
      {
        heading: "Auto: OC to obowiązek, AC to wybór",
        body:
          "OC jest obowiązkowe i chroni nie Was, tylko osoby, którym wyrządzicie szkodę — za to bez limitu z Waszej kieszeni. Przerwa w OC choćby o jeden dzień oznacza karę od Ubezpieczeniowego Funduszu Gwarancyjnego, liczoną w tysiącach złotych.\n\nAC jest dobrowolne i chroni Wasze auto: kradzież, stłuczka z własnej winy, grad. Przy starszym samochodzie wartym kilka tysięcy złotych AC bywa nieopłacalne — przy nowym to często rozsądek.",
      },
      {
        heading: "Życie: dla tych, którzy zostaną",
        body:
          "Ubezpieczenie na życie ma sens wtedy, gdy ktoś zależy od Waszych dochodów — dzieci, partner, kredyt hipoteczny. Prosta miara: suma ubezpieczenia powinna pozwolić rodzinie spłacić zobowiązania i utrzymać się przez kilka lat.\n\nJeśli oboje pracujecie i macie małe dzieci, warto ubezpieczyć oboje rodziców — utrata każdego z dochodów byłaby ciosem.",
      },
      {
        heading: "Majątek: dom to zwykle wszystko, co macie",
        body:
          "Przy ubezpieczeniu mieszkania kluczowa jest suma ubezpieczenia — powinna odpowiadać realnej wartości odtworzenia, nie kwocie sprzed dziesięciu lat. Zaniżona suma to zaniżone odszkodowanie.\n\nPolisy różnią się bardziej wyłączeniami niż ceną, dlatego warto porozmawiać z agentem, który porówna oferty wielu towarzystw — w multiagencji, takiej jak Polfinance, można zestawić kilka propozycji obok siebie i dopasować ochronę do Waszej sytuacji zamiast kupować w ciemno.",
      },
    ],
    task:
      "Zrób listę polis, które macie: co, do kiedy, jaka suma ubezpieczenia. Zaznacz jedną rzecz, której ochrony brakuje albo która wygląda na przestarzałą — i wyjaśnij ją w tym tygodniu.",
    sources: [
      "Ubezpieczeniowy Fundusz Gwarancyjny",
      "Rzecznik Finansowy — ubezpieczenia",
    ],
  },
  {
    day: 7,
    title: "Wspólne finanse w związku — jak rozmawiać o pieniądzach",
    teaser:
      "Pieniądze to jeden z najczęstszych powodów kłótni w domu — a przecież gracie do jednej bramki. Dziś kilka zasad, dzięki którym rozmowa o finansach przestaje być polem minowym.",
    sections: [
      {
        heading: "Randka finansowa raz w miesiącu",
        body:
          "Najgorsze rozmowy o pieniądzach to te wymuszone kryzysem — przy niezapłaconym rachunku, w emocjach. Zamiast tego umówcie się na stały termin: 30 minut raz w miesiącu, kawa, spokój, bez dzieci.\n\nPlan spotkania: co wpłynęło, co wydaliśmy, co nas zaskoczyło, co zmieniamy w przyszłym miesiącu. To wszystko. Bez oskarżeń, bez wypominania — patrzycie na liczby, nie na siebie.",
      },
      {
        heading: "Model trzech kont",
        body:
          "Wiele par dobrze funkcjonuje w układzie: konto wspólne plus dwa osobiste. Na wspólne oboje przelewacie ustaloną kwotę lub procent dochodów — z niego idą czynsz, jedzenie, dzieci, oszczędności rodziny.\n\nTo, co zostaje na kontach osobistych, to strefa wolności: każde z Was wydaje bez tłumaczenia się. Koniec dyskusji o tym, czy gra komputerowa albo wizyta u fryzjera była „potrzebna”.",
      },
      {
        heading: "Jedno tabu, którego nie wolno mieć",
        body:
          "Różne style wydawania są w porządku. Ukrywanie długów i zobowiązań — nie. Pożyczka, o której partner nie wie, prawie zawsze wychodzi na jaw w najgorszym momencie, na przykład przy wniosku o wspólny kredyt.\n\nJeśli macie zaległe tematy, randka finansowa to dobre miejsce, by je wyłożyć na stół. Zasada: mówimy o faktach i szukamy planu, nie winnego.",
      },
    ],
    task:
      "Umówcie się dziś na pierwszą randkę finansową — konkretny dzień i godzina w kalendarzu, 30 minut. Przygotujcie tylko jedno: wyciągi z ostatniego miesiąca.",
    sources: [
      "Narodowy Bank Polski (edukacja ekonomiczna)",
    ],
  },
  {
    day: 8,
    title: "Dzieci a pieniądze: 800+, kieszonkowe i konto dziecka",
    teaser:
      "Dzieci uczą się pieniędzy od Was — nie z podręczników, tylko z tego, co widzą w domu. Dziś o tym, jak mądrze ułożyć 800+, kieszonkowe i pierwsze konto.",
    sections: [
      {
        heading: "800+ z planem",
        body:
          "Świadczenie 800+ wpływa co miesiąc i łatwo rozpływa się w bieżących wydatkach. Warto nadać mu strukturę: część na bieżące potrzeby dziecka, a stała część — nawet 100–200 zł — na osobne konto oszczędnościowe z myślą o jego przyszłości.\n\nOdkładane 200 zł miesięcznie przez 18 lat to ponad 43 000 zł samych wpłat — realny start w dorosłość. Wniosek o świadczenie składa się przez ZUS, m.in. w aplikacji mZUS lub przez bankowość elektroniczną.",
      },
      {
        heading: "Kieszonkowe, czyli trening na małych kwotach",
        body:
          "Kieszonkowe to nie nagroda ani łapówka — to poligon. Ustalcie stałą kwotę i stały dzień wypłaty, dopasowane do wieku, na przykład 10–20 zł tygodniowo dla młodszego ucznia.\n\nNajważniejsza zasada: gdy dziecko wyda wszystko pierwszego dnia, nie dosypujcie. Lekcja „zabrakło mi do soboty” za 15 zł jest bezcenna — dużo tańsza niż ta sama lekcja za 15 000 zł w dorosłości.",
      },
      {
        heading: "Pierwsze konto",
        body:
          "Od 13. roku życia nastolatek może mieć własne konto z kartą (za zgodą rodzica), a Wy zachowujecie podgląd i kontrolę limitów. To bezpieczne środowisko do nauki: przelewy, płatności telefonem, planowanie.\n\nMłodsze dzieci świetnie uczą się na trzech słoikach: wydaję, oszczędzam, dzielę się. Prosty podział kieszonkowego buduje nawyki na całe życie.",
      },
    ],
    task:
      "Ustalcie dziś, jaka część 800+ będzie co miesiąc odkładana na przyszłość dziecka, i ustawcie na tę kwotę zlecenie stałe na osobne konto.",
    sources: [
      "ZUS — świadczenia dla rodzin",
      "Ministerstwo Rodziny, Pracy i Polityki Społecznej",
    ],
  },
  {
    day: 9,
    title: "Cele: jak marzenie zamienić w kwotę i datę",
    teaser:
      "„Kiedyś pojedziemy nad morze, kiedyś wymienimy auto” — „kiedyś” to najdroższe słowo w finansach. Dziś nauczysz się zamieniać marzenia w plan z kwotą i terminem.",
    sections: [
      {
        heading: "Marzenie kontra cel",
        body:
          "Marzenie brzmi: „chcielibyśmy pojechać na porządne wakacje”. Cel brzmi: „wakacje nad Bałtykiem, 6000 zł, wyjazd w lipcu przyszłego roku”. Różnica? Cel ma kwotę i datę — więc da się policzyć, co robić już dziś.\n\n6000 zł za 10 miesięcy to 600 zł miesięcznie. Nagle rozmowa przestaje być o marzeniach, a zaczyna o konkretach: skąd weźmiemy te 600 zł?",
      },
      {
        heading: "Trzy horyzonty",
        body:
          "Warto mieć cele w trzech horyzontach. Krótkie (do roku): wakacje, nowa pralka, prezenty świąteczne bez karty kredytowej. Średnie (1–5 lat): samochód, remont, wkład własny. Długie (5+ lat): edukacja dzieci, emerytura.\n\nNa każdy horyzont — osobna „przegródka”: dziś wiele banków pozwala tworzyć podkonta lub cele oszczędnościowe z własnymi nazwami. Pieniądze z etykietą „Wakacje 2027” trudniej wydać na promocję w markecie.",
      },
      {
        heading: "Gdy kwota nie wychodzi",
        body:
          "Policzyliście i miesięczna rata celu jest nierealna? Macie trzy suwaki: przesunąć datę, zmniejszyć kwotę (może tydzień zamiast dwóch?) albo znaleźć dodatkowe wpływy.\n\nTo nie porażka — to właśnie jest planowanie. Cel, który się nie spina na papierze, na pewno nie spiąłby się w rzeczywistości. Lepiej wiedzieć to dziś niż za rok.",
      },
    ],
    task:
      "Wybierzcie jeden wspólny cel i zapiszcie go w formacie: co, ile złotych, do kiedy, ile miesięcznie. Załóżcie na niego osobne podkonto lub cel w banku.",
    sources: [
      "Narodowy Bank Polski (edukacja ekonomiczna)",
    ],
  },
  {
    day: 10,
    title: "Inflacja — dlaczego gotówka topnieje",
    teaser:
      "Te same zakupy, wyższy rachunek — to inflacja w akcji. Dziś zrozumiesz, czemu pieniądze w szufladzie tracą wartość i co z tym robić.",
    sections: [
      {
        heading: "Cichy podatek od czekania",
        body:
          "Inflacja to wzrost przeciętnych cen. Jeśli wynosi 5% rocznie, to koszyk zakupów, który dziś kosztuje 100 zł, za rok będzie kosztował około 105 zł. Wasze 100 zł nadal wygląda tak samo — ale kupuje mniej.\n\nPrzykład: 10 000 zł trzymane w gotówce przy inflacji 5% traci w rok około 500 zł siły nabywczej. Nikt Wam ich nie zabrał — po prostu wyparowały.",
      },
      {
        heading: "Kto mierzy i kto pilnuje",
        body:
          "Inflację w Polsce mierzy Główny Urząd Statystyczny, porównując ceny setek towarów i usług miesiąc do miesiąca. Nad stabilnością cen czuwa Narodowy Bank Polski, którego cel inflacyjny to 2,5% z dopuszczalnym odchyleniem o 1 punkt procentowy.\n\nGdy inflacja rośnie za mocno, NBP zwykle podnosi stopy procentowe — dlatego drożeją wtedy raty kredytów, ale rosną też odsetki na lokatach.",
      },
      {
        heading: "Co to znaczy dla Waszych pieniędzy",
        body:
          "Wniosek pierwszy: gotówka „pod materacem” to gwarantowana strata — nawet zwykłe konto oszczędnościowe odrabia część inflacji. Wniosek drugi: porównując lokatę z inflacją, patrzcie na realny zysk. Lokata 5% przy inflacji 4% daje realnie około 1% — i to jeszcze przed podatkiem od zysków.\n\nWniosek trzeci: im dłuższy horyzont oszczędzania, tym bardziej trzeba myśleć o ochronie pieniędzy przed inflacją. O tym więcej w lekcjach o emeryturze i inwestowaniu.",
      },
    ],
    task:
      "Sprawdź aktualny poziom inflacji na stronie GUS i policz, ile siły nabywczej straci w rok kwota, którą trzymacie poza kontem oszczędnościowym.",
    sources: [
      "Główny Urząd Statystyczny — wskaźniki cen",
      "Narodowy Bank Polski",
    ],
  },
  {
    day: 11,
    title: "IKE i IKZE — emerytura bez paniki",
    teaser:
      "Emerytura z ZUS będzie zapewne skromniejsza niż dzisiejsza pensja — ale państwo daje dwa konta z ulgami podatkowymi, żeby to nadrobić. Poznaj IKE i IKZE po ludzku.",
    sections: [
      {
        heading: "Dlaczego warto zacząć wcześnie",
        body:
          "Prognozy pokazują, że przyszłe emerytury z ZUS będą stanowić coraz mniejszy procent ostatniej pensji. To nie powód do paniki — to powód do planu.\n\nCzas jest tu najsilniejszym sojusznikiem: odkładanie 300 zł miesięcznie przez 30 lat to 108 000 zł samych wpłat, a doliczając zyski z pomnażania, kwota może być znacznie większa. Ta sama suma zbierana przez 10 lat wymagałaby już 900 zł miesięcznie.",
      },
      {
        heading: "IKE i IKZE w skrócie",
        body:
          "IKE (Indywidualne Konto Emerytalne): wpłacasz, pomnażasz, a wypłacając po 60. roku życia (i spełnieniu warunków) nie płacisz podatku od zysków kapitałowych, tzw. podatku Belki.\n\nIKZE (Indywidualne Konto Zabezpieczenia Emerytalnego): wpłaty odliczasz od dochodu w rocznym PIT — ulga wraca do Was już teraz, przy rozliczeniu. Przy wypłacie po 65. roku życia płaci się zryczałtowany podatek 10%. Oba konta mają roczne limity wpłat, ogłaszane co roku — sprawdźcie aktualne przed wpłatą. Można mieć oba jednocześnie.",
      },
      {
        heading: "Od czego zacząć",
        body:
          "IKE i IKZE to tylko „opakowania” — w środku może być rachunek oszczędnościowy, fundusze czy rachunek maklerski, w zależności od instytucji. Zacznijcie od pytania: wolimy ulgę w PIT co roku (IKZE) czy brak podatku na końcu (IKE)?\n\nPamiętajcie: to lekcja edukacyjna, nie porada inwestycyjna ani podatkowa. Decyzje dopasujcie do własnej sytuacji, a w razie wątpliwości skonsultujcie się ze specjalistą.",
      },
    ],
    task:
      "Sprawdź na swoim koncie w ZUS (PUE/eZUS) prognozę swojej emerytury, a potem porównaj aktualne limity wpłat na IKE i IKZE na stronach rządowych.",
    sources: [
      "ZUS — emerytury i prognoza świadczeń",
      "Serwis Rzeczypospolitej Polskiej — finanse",
    ],
  },
  {
    day: 12,
    title: "Pierwsze inwestowanie — zasady bezpieczeństwa",
    teaser:
      "Inwestowanie to nie kasyno ani sport dla wybranych — ale ma swoje zasady bezpieczeństwa, jak jazda samochodem. Dziś poznasz te najważniejsze, zanim wsiądziesz za kółko.",
    sections: [
      {
        heading: "Najpierw fundamenty",
        body:
          "Zanim zainwestujecie pierwszą złotówkę, sprawdźcie trzy rzeczy: macie poduszkę bezpieczeństwa (lekcja 2), spłaciliście drogie długi (lekcja 5) i inwestujecie pieniądze, których nie będziecie potrzebować przez co najmniej kilka lat.\n\nDlaczego to takie ważne? Bo rynki potrafią spadać. Kto inwestuje pieniądze na czynsz, ten przy pierwszym spadku sprzedaje ze stratą i w panice. Kto inwestuje nadwyżki — może spokojnie przeczekać.",
      },
      {
        heading: "Trzy żelazne zasady",
        body:
          "Zasada 1: nie wkładaj wszystkiego do jednego koszyka. Rozłożenie pieniędzy na różne rodzaje inwestycji zmniejsza ryzyko, że jedna wpadka zabierze wszystko.\n\nZasada 2: nie inwestuj w to, czego nie rozumiesz. Jeśli nie umiecie w dwóch zdaniach wyjaśnić dziecku, na czym zarabia dany produkt — to nie jest produkt dla Was.\n\nZasada 3: pilnuj kosztów. Opłata 2% rocznie zamiast 0,5% przez 20 lat potrafi zjeść dziesiątki tysięcy złotych z wyniku.",
      },
      {
        heading: "Tylko legalnie i z głową",
        body:
          "Korzystajcie wyłącznie z instytucji nadzorowanych przez Komisję Nadzoru Finansowego — listę podmiotów z licencją i listę ostrzeżeń publicznych znajdziecie na stronie KNF. Obietnica „gwarantowane 20% rocznie bez ryzyka” to nie okazja, tylko czerwona flaga.\n\nI ważne zastrzeżenie: ta lekcja to edukacja, nie porada inwestycyjna. Każda inwestycja wiąże się z ryzykiem, a decyzje podejmujecie na własną odpowiedzialność, najlepiej po dokładnym poznaniu produktu.",
      },
    ],
    task:
      "Zanim cokolwiek kupisz: sprawdź na stronie KNF listę ostrzeżeń publicznych i upewnij się, że instytucja, którą rozważasz, ma licencję i nie figuruje na tej liście.",
    sources: [
      "KNF — ostrzeżenia publiczne",
      "Analizy.pl — dane o funduszach i oszczędzaniu",
    ],
  },
  {
    day: 13,
    title: "Jak nie dać się nabrać — scam, chwilówki i „okazje”",
    teaser:
      "Oszuści nie wyglądają jak oszuści — wyglądają jak bank, urząd albo świetna inwestycja. Dziś nauczysz się rozpoznawać pułapki, zanim klikniesz.",
    sections: [
      {
        heading: "Trzy czerwone flagi",
        body:
          "Flaga pierwsza: presja czasu. „Oferta ważna tylko dziś”, „za chwilę stracisz środki” — uczciwe instytucje nie zmuszają do decyzji w minutę.\n\nFlaga druga: gwarantowany wysoki zysk. Bez ryzyka nie ma 15% miesięcznie. Nigdy.\n\nFlaga trzecia: prośba o kody i hasła. Prawdziwy pracownik banku nigdy nie poprosi Was o kod BLIK, hasło do konta ani o zainstalowanie aplikacji „do sprawdzenia bezpieczeństwa”. Taka prośba równa się: rozłączamy się i dzwonimy do banku na oficjalny numer z jego strony.",
      },
      {
        heading: "Chwilówki i raty-pułapki",
        body:
          "„Pierwsza pożyczka za darmo” bywa wejściem do spirali: druga i trzecia mają już RRSO liczone w dziesiątkach, a bywało, że w setkach procent. Pożyczone 2000 zł potrafi urosnąć do kwoty kilkukrotnie wyższej.\n\nZanim podpiszesz jakąkolwiek umowę pożyczki: sprawdź RRSO, całkowitą kwotę do spłaty i to, czy firma nie figuruje na liście ostrzeżeń KNF. Jeśli brakuje Wam do wypłaty regularnie — to problem budżetu (lekcje 1 i 4), a chwilówka go tylko pogłębi.",
      },
      {
        heading: "Sprawdzaj, zanim zaufasz",
        body:
          "Dwa nawyki chronią przed większością oszustw. Pierwszy: adres strony wpisujcie sami zamiast klikać w linki z SMS-ów i maili — podrobione strony banków różnią się jedną literą.\n\nDrugi: każdą „okazję inwestycyjną” i firmę pożyczkową sprawdzajcie na liście ostrzeżeń publicznych KNF oraz w wyszukiwarce z dopiskiem „opinie oszustwo”. Pięć minut sprawdzania kontra oszczędności życia — ten rachunek zawsze się opłaca.",
      },
    ],
    task:
      "Porozmawiajcie dziś z bliskimi (także z rodzicami i dziadkami!) o zasadzie: nikt z banku nigdy nie prosi o kod BLIK, hasło ani instalację aplikacji. Jedna rozmowa może uratować czyjeś oszczędności.",
    sources: [
      "KNF — ostrzeżenia publiczne",
      "UOKiK — ostrzeżenia konsumenckie",
    ],
  },
  {
    day: 14,
    title: "Przegląd roczny finansów rodziny",
    teaser:
      "Raz w roku warto spojrzeć na finanse z lotu ptaka: co się udało, co przecieka, co zmieniamy. To ostatnia lekcja — i rytuał, który zostanie z Wami na lata.",
    sections: [
      {
        heading: "Bilans: gdzie jesteśmy",
        body:
          "Usiądźcie razem i spiszcie dwie kolumny. Po lewej wszystko, co macie: oszczędności, poduszka, konta dzieci, wartość auta, mieszkania. Po prawej wszystko, co jesteście winni: hipoteka, raty, karty.\n\nRóżnica to majątek netto Waszej rodziny. Sama liczba jest mniej ważna niż kierunek: czy rok do roku rośnie? Zapiszcie ją — za rok porównacie i zobaczycie czarno na białym efekt swojej pracy.",
      },
      {
        heading: "Wielki przegląd umów",
        body:
          "Raz w roku sprawdźcie wszystko, co kosztuje Was cyklicznie: ubezpieczenia (czy sumy nadal odpowiadają wartości majątku?), telefon i internet (czy po końcu umowy nie płacicie taryfy „dla zapominalskich”?), prąd i gaz, subskrypcje, konta bankowe i ich opłaty.\n\nDobra praktyka: przed przedłużeniem każdej polisy i umowy zbierzcie choć dwie oferty konkurencji. Godzina pracy potrafi przynieść kilkaset złotych oszczędności rocznie.",
      },
      {
        heading: "Plan na kolejny rok",
        body:
          "Na koniec trzy pytania. Co było naszym największym sukcesem finansowym w tym roku? Co było największym przeciekiem? Jaki jest nasz jeden najważniejszy cel na kolejny rok — z kwotą i datą, jak w lekcji 9?\n\nZapiszcie odpowiedzi i wróćcie do nich za dwanaście miesięcy. Finanse rodziny to nie sprint i nie magia — to właśnie takie spokojne, powtarzane co roku rytuały. Gratulacje, ukończyliście Szkołę finansów domowych.",
      },
    ],
    task:
      "Policzcie dziś majątek netto rodziny (co mamy minus co jesteśmy winni) i zapiszcie wynik z datą. Ustawcie w kalendarzu przypomnienie o przeglądzie rocznym za 12 miesięcy.",
    sources: [
      "Narodowy Bank Polski (edukacja ekonomiczna)",
      "Rzecznik Finansowy",
    ],
  },
];
